import {
  type HostedConnector,
  hostedConnectorOf,
  type RedirectTrust,
  redirectHostOf,
  redirectTrustOf,
} from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { Schema } from "effect"
import { ApiError, apiJson, Instant, toDate } from "./api"

/**
 * The web side of the external MCP server's OAuth flow (external MCP spec §2.5, §6; decision 0046). The Worker's
 * authorization server (`/api/auth/oauth2/*`) sends the browser to two SPA pages:
 *   /oauth/continue?<signed query>  not signed in yet: sign in, then re-run authorize with the client's own params
 *   /oauth/consent?<signed query>   signed in, no consent yet: Allow → POST /api/auth/oauth2/consent
 * Settings › Security › Connected apps reads `get-consents` and disconnects with `POST /api/me/oauth/revoke
 * { clientId }` plus `delete-consent` (which alone leaves refresh tokens valid).
 */

export const OAUTH_CONTINUE_PATH = "/oauth/continue"
export const OAUTH_CONSENT_PATH = "/oauth/consent"
const AUTHORIZE_PATH = "/api/auth/oauth2/authorize"

/** A same-origin href into the OAuth pages: sign-in and sign-up leave for it with a full page load (see `rawSearch`). */
export const isOAuthPage = (href: string): boolean =>
  [OAUTH_CONTINUE_PATH, OAUTH_CONSENT_PATH].some(
    (path) => href === path || href.startsWith(`${path}?`) || href.startsWith(`${path}#`),
  )

// ---- The signed query, untouched -----------------------------------------------------------------------------------

type BootLocation = { readonly pathname: string; readonly search: string }

/**
 * The address bar as the page was loaded, read when this module is first imported, before the router mounts. The
 * router re-serializes search params on mount (repeated keys become a JSON array, number-like strings are re-printed)
 * and replaces the URL with that, which would break the provider's signature (`sig` over every `ba_param`). The OAuth
 * pages are always entered by a full page load (a Worker redirect, or `window.location.assign` from sign-in), so this
 * is their real query.
 */
const BOOT: BootLocation | null =
  typeof window === "undefined" || !window.location
    ? null
    : { pathname: window.location.pathname, search: window.location.search }

/** The raw query (no leading `?`) of the OAuth page at `pathname`: the boot one, else the router's own string. */
export const rawSearch = (
  pathname: string,
  routerSearchStr: string,
  boot: BootLocation | null = BOOT,
): string => (boot && boot.pathname === pathname ? boot.search : routerSearchStr).replace(/^\?/, "")

// ---- /oauth/continue -----------------------------------------------------------------------------------------------

/** The authorization request params a client sends (spec §6.1). Everything else is the provider's signing. */
const CLIENT_PARAMS = [
  "response_type",
  "client_id",
  "redirect_uri",
  "scope",
  "state",
  "code_challenge",
  "code_challenge_method",
  "resource",
  "prompt",
] as const

/**
 * `prompt` minus the values that send a signed-in person back to the login page: `login` (spec §6.1) and `create`
 * (the provider sends it to the same page, so keeping it would loop). Empty → dropped.
 */
const promptWithoutLogin = (prompt: string): string =>
  prompt
    .split(" ")
    .filter((p) => p && p !== "login" && p !== "create")
    .join(" ")

/**
 * The authorize URL that re-runs the client's request with the session: the client's own params, in their order, and
 * without `sig`, `exp`, `ba_*` and anything else the provider added. Null when the query has no `client_id`.
 */
export const authorizeUrl = (search: string): string | null => {
  const from = new URLSearchParams(search)
  if (!from.get("client_id")) return null
  const out = new URLSearchParams()
  for (const [key, value] of from) {
    if (!(CLIENT_PARAMS as ReadonlyArray<string>).includes(key)) continue
    if (key === "prompt") {
      const rest = promptWithoutLogin(value)
      if (rest) out.append(key, rest)
      continue
    }
    out.append(key, value)
  }
  return `${AUTHORIZE_PATH}?${out.toString()}`
}

/** `/sign-in`'s `redirect` for an OAuth page: back through `/oauth/continue` with the same query. */
export const continueHref = (search: string): string =>
  search ? `${OAUTH_CONTINUE_PATH}?${search}` : OAUTH_CONTINUE_PATH

const BOUNCE_KEY = "minga.oauth.bounces"
const BOUNCE_WINDOW_MS = 60_000
const BOUNCE_LIMIT = 5

/**
 * Counts this tab's trips through `/oauth/continue` with a session. If the authorization server keeps sending a
 * signed-in person back here (it can't see the session), the page stops instead of looping. True = keep going.
 */
export const recordBounce = (now = Date.now()): boolean => {
  try {
    const raw = window.sessionStorage.getItem(BOUNCE_KEY)
    const list: unknown = raw ? JSON.parse(raw) : []
    const recent = (Array.isArray(list) ? list : []).filter(
      (t): t is number => typeof t === "number" && now - t < BOUNCE_WINDOW_MS,
    )
    recent.push(now)
    window.sessionStorage.setItem(BOUNCE_KEY, JSON.stringify(recent))
    return recent.length <= BOUNCE_LIMIT
  } catch {
    return true
  }
}

// ---- /oauth/consent ------------------------------------------------------------------------------------------------

export type ConsentRequest = {
  readonly clientId: string
  readonly scopes: ReadonlyArray<string>
  /** `localhost:53122`: where the code goes. Null when the query has no readable `redirect_uri`. */
  readonly redirectHost: string | null
  /**
   * The verified connector (Claude, ChatGPT, Every Agent) whose documented callback is exactly the `redirect_uri`, or
   * null. `redirect_uri` is covered by the query's signature.
   */
  readonly connector: HostedConnector | null
  /**
   * Where the code goes, by the `redirect_uri` alone: `verified` (a listed callback), `local` (loopback, this
   * computer) or `unverified` (any other `https:` host, decision 0046 amended 2026-10-21; also anything unreadable).
   */
  readonly trust: RedirectTrust
  /** The signed query's expiry (`exp`, seconds) in millis, when present. */
  readonly expiresAt: number | null
}

export const redirectHost = (uri: string | null): string | null => redirectHostOf(uri)

/** What the consent page shows, read from the signed query. Null when it names no client. */
export const consentRequest = (search: string): ConsentRequest | null => {
  const q = new URLSearchParams(search)
  const clientId = q.get("client_id")
  if (!clientId) return null
  const exp = Number(q.get("exp"))
  return {
    clientId,
    scopes: (q.get("scope") ?? "").split(" ").filter(Boolean),
    redirectHost: redirectHost(q.get("redirect_uri")),
    connector: hostedConnectorOf(q.get("redirect_uri")),
    trust: redirectTrustOf(q.get("redirect_uri")),
    expiresAt: Number.isFinite(exp) && exp > 0 ? exp * 1000 : null,
  }
}

/** `GET /api/auth/oauth2/public-client`: the client's self-declared metadata (RFC 7591 names). */
export const PublicClient = Schema.Struct({
  client_id: Schema.String,
  client_name: Schema.optionalKey(Schema.NullOr(Schema.String)),
  client_uri: Schema.optionalKey(Schema.NullOr(Schema.String)),
  logo_uri: Schema.optionalKey(Schema.NullOr(Schema.String)),
})
export type PublicClient = typeof PublicClient.Type

export const publicClientQuery = (clientId: string) =>
  queryOptions({
    queryKey: ["oauth", "public-client", clientId],
    queryFn: () =>
      apiJson(`/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`, PublicClient),
    retry: false,
    staleTime: 5 * 60_000,
  })

/** The name a client registered (self-declared, so the page also shows where it sends you), or a fallback. */
export const clientDisplayName = (client: PublicClient | null | undefined, clientId?: string): string =>
  client?.client_name?.trim() || (clientId ? `App ${clientId.slice(0, 8)}` : "This app")

/** A registered name as the page may quote it ("calls itself …"): trimmed, one line, at most 60 characters. */
export const quotedClientName = (client: PublicClient | null | undefined): string | null => {
  const name = (client?.client_name ?? "").replace(/\s+/g, " ").trim()
  if (!name) return null
  return name.length > 60 ? `${name.slice(0, 59)}…` : name
}

export type ConsentClient =
  /** A listed vendor callback: named by the vendor and its host, whatever it registered as. */
  | { readonly kind: "verified"; readonly name: string; readonly host: string }
  /** Loopback: a CLI client on this computer, by the name it registered. */
  | { readonly kind: "local"; readonly name: string }
  /** Any other `https:` host: named by that host (from `redirect_uri`); the registered name is only "calls itself". */
  | { readonly kind: "unverified"; readonly host: string; readonly calls: string | null }

/**
 * Who the consent page says is asking, by where the code goes, never by the name the client registered (decision 0046,
 * amended 2026-10-12 and 2026-10-21):
 * - a verified connector by its product name and callback host ("Claude", "claude.ai");
 * - a loopback client by its self-declared name, "on this computer" (the code goes to a port on the person's machine);
 * - any other client by the host of its `redirect_uri`, unverified, with its registered name only as "calls itself".
 */
export const consentClient = (
  request: Pick<ConsentRequest, "clientId" | "connector" | "trust" | "redirectHost">,
  client: PublicClient | null | undefined,
): ConsentClient => {
  if (request.trust === "verified" && request.connector)
    return { kind: "verified", name: request.connector.name, host: request.connector.host }
  if (request.trust === "local") return { kind: "local", name: clientDisplayName(client, request.clientId) }
  return {
    kind: "unverified",
    host: request.redirectHost ?? "an unknown address",
    calls: quotedClientName(client),
  }
}

const ConsentResult = Schema.Struct({
  url: Schema.optionalKey(Schema.String),
  redirect_uri: Schema.optionalKey(Schema.String),
})

/**
 * `POST /api/auth/oauth2/consent { accept, oauth_query }` → where the browser goes next: the client's loopback
 * callback with a code (Allow) or `access_denied` (Cancel). `oauthQuery` is the page's query, unchanged.
 */
export const postConsent = async (accept: boolean, oauthQuery: string): Promise<string> => {
  const result = await apiJson("/api/auth/oauth2/consent", ConsentResult, {
    method: "POST",
    body: { accept, oauth_query: oauthQuery },
  })
  const url = result.url ?? result.redirect_uri
  if (!url) throw new ApiError(500, "Minga didn't say where to go next.")
  return url
}

export const EXPIRED_TEXT =
  "This link expired. Start the connection again from the app you were connecting (your terminal, Claude or ChatGPT)."

/**
 * An expired or tampered signed query (400 `invalid_signature`, `invalid_request`) or an unknown client (404): the
 * person starts over in the terminal.
 */
export const isExpiredLinkError = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 400 || error.status === 404)

// ---- Settings › Security › Connected apps --------------------------------------------------------------------------

/** A row of `GET /api/auth/oauth2/get-consents` (the provider's `oauthConsent` model). */
const ConsentRow = Schema.Struct({
  id: Schema.String,
  clientId: Schema.String,
  scopes: Schema.optionalKey(Schema.NullOr(Schema.Union([Schema.Array(Schema.String), Schema.String]))),
  createdAt: Schema.optionalKey(Schema.NullOr(Instant)),
  updatedAt: Schema.optionalKey(Schema.NullOr(Instant)),
})
const ConsentRows = Schema.NullOr(Schema.Array(ConsentRow))

export type ConnectedApp = {
  readonly consentId: string
  readonly clientId: string
  /** "Claude (claude.ai)" when verified; the host(s) its codes go to when unverified; the registered name when local. */
  readonly name: string
  /** As on the consent page; null when the lookup failed (the name is then the registered one, as before). */
  readonly trust: RedirectTrust | null
  /** An unverified app's registered name ("calls itself …"), else null. */
  readonly calls: string | null
  readonly scopes: ReadonlyArray<string>
  readonly connectedAt: number | null
}

const scopeList = (scopes: ReadonlyArray<string> | string | null | undefined): ReadonlyArray<string> =>
  typeof scopes === "string" ? scopes.split(/[\s,]+/).filter(Boolean) : [...(scopes ?? [])]

/** The client's registered name, or null when it is gone or unreadable (the row still shows, by id). */
const clientName = async (clientId: string): Promise<string | null> => {
  try {
    const client = await apiJson(
      `/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`,
      PublicClient,
    )
    return client.client_name?.trim() || null
  } catch {
    return null
  }
}

/** `GET /api/me/oauth/clients`: who each consented client is, by its stored callbacks. */
const StoredClients = Schema.Struct({
  clients: Schema.Array(
    Schema.Struct({
      clientId: Schema.String,
      connector: Schema.NullOr(Schema.Struct({ name: Schema.String, host: Schema.String })),
      trust: Schema.optionalKey(Schema.Literals(["verified", "local", "unverified"])),
      hosts: Schema.optionalKey(Schema.Array(Schema.String)),
    }),
  ),
})
type StoredClient = (typeof StoredClients.Type)["clients"][number]

/** clientId → its stored identity; empty when the lookup fails (names fall back to the registered ones). */
const storedClients = async (): Promise<ReadonlyMap<string, StoredClient>> => {
  try {
    const { clients } = await apiJson("/api/me/oauth/clients", StoredClients)
    return new Map(clients.map((c) => [c.clientId, c]))
  } catch {
    return new Map()
  }
}

/** One app row's name and trust, from its stored identity and its registered name. */
const appIdentity = (
  stored: StoredClient | undefined,
  registered: string | null,
  clientId: string,
): Pick<ConnectedApp, "name" | "trust" | "calls"> => {
  const fallback = registered ?? `App ${clientId.slice(0, 8)}`
  if (stored?.connector)
    return { name: `${stored.connector.name} (${stored.connector.host})`, trust: "verified", calls: null }
  if (stored?.trust === "unverified")
    return {
      name: stored.hosts && stored.hosts.length > 0 ? stored.hosts.join(", ") : "Unknown address",
      trust: "unverified",
      calls: registered,
    }
  return { name: fallback, trust: stored?.trust ?? null, calls: null }
}

/**
 * The consented apps, named as the consent page names them (decision 0046 §9, amended 2026-10-21): a verified connector
 * by its callback ("Claude (claude.ai)"), an unverified app by the host its codes go to (with its registered name only
 * as "calls itself"), a loopback client by its self-declared name.
 */
export const connectedAppsQuery = queryOptions({
  queryKey: ["account", "oauth-consents"],
  queryFn: async (): Promise<ReadonlyArray<ConnectedApp>> => {
    const [rows, stored] = await Promise.all([
      apiJson("/api/auth/oauth2/get-consents", ConsentRows).then((r) => r ?? []),
      storedClients(),
    ])
    const registered = await Promise.all(
      rows.map((r) => (stored.get(r.clientId)?.connector ? null : clientName(r.clientId))),
    )
    return rows
      .map((r, i) => ({
        consentId: r.id,
        clientId: r.clientId,
        ...appIdentity(stored.get(r.clientId), registered[i] ?? null, r.clientId),
        scopes: scopeList(r.scopes),
        connectedAt: toDate(r.createdAt ?? r.updatedAt ?? null)?.getTime() ?? null,
      }))
      .sort((a, b) => (b.connectedAt ?? 0) - (a.connectedAt ?? 0))
  },
  staleTime: 30_000,
})

// Disconnect lives in `lib/agents.ts` (decision 0148): one server call revokes the grant, removes the consent, turns the
// app's agents off and ends its message subscriptions.

// ---- Chat: messages sent through an MCP client (spec §4.6, §6.4) ---------------------------------------------------

export type MessageOrigin = { readonly author: string | null; readonly via: string | null }

/** `metadata: { author, via }` of a lead-chat message; only the server sets them. */
export const messageOrigin = (metadata: unknown): MessageOrigin => {
  const m = metadata && typeof metadata === "object" ? (metadata as Record<string, unknown>) : {}
  return {
    author: typeof m.author === "string" && m.author ? m.author : null,
    via: typeof m.via === "string" && m.via ? m.via : null,
  }
}

const CLIENT_LABELS: Readonly<Record<string, string>> = {
  "claude-code": "Claude Code",
  codex: "Codex",
  claude: "Claude",
  chatgpt: "ChatGPT",
  every: "Every Agent",
  // decision 0123: a browser agent through WebMCP on the Minga page
  webmcp: "Browser agent",
}

/**
 * `mcp:claude-code` → "Claude Code", `mcp:codex` → "Codex", `mcp:claude` → "Claude" (claude.ai and the apps),
 * `mcp:chatgpt` → "ChatGPT", `mcp:<slug>` → the slug, `mcp:mcp` → "MCP".
 */
export const viaLabel = (via: string | null | undefined): string | null => {
  if (!via?.startsWith("mcp:")) return null
  const client = via.slice("mcp:".length).trim()
  if (!client || client === "mcp") return "MCP"
  return CLIENT_LABELS[client] ?? client
}
